// src/pages/HomePage.js

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Button,
  Card
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { Parallax } from 'react-parallax';

// ─────────────────────────────────────────────
// FAQ DATA  (drives both the UI and JSON-LD schema)
// ─────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    question: 'What is Benign Fasciculation Syndrome (BFS)?',
    answer:
      'Benign Fasciculation Syndrome (BFS) is a neurological condition characterised by persistent, involuntary muscle twitches (fasciculations) that occur in the absence of any underlying serious disease. The twitches can affect any muscle group — calves, thighs, eyelids, tongue — and may be constant or intermittent. Despite feeling alarming, BFS is considered a benign (non-threatening) condition with no link to muscle wasting or neurological deterioration.'
  },
  {
    question: 'Is BFS the same as ALS (Lou Gehrig\'s Disease)?',
    answer:
      'No. BFS and ALS are very different conditions. ALS involves progressive muscle weakness, wasting (atrophy), and loss of function. BFS involves twitching in muscles that remain strong and functional. A key diagnostic indicator is that BFS fasciculations typically occur in muscles with normal strength, while ALS-related fasciculations are accompanied by weakness and atrophy. A neurologist can confirm the distinction through clinical examination and, if needed, an EMG test.'
  },
  {
    question: 'What are the most common symptoms of BFS?',
    answer:
      'The hallmark symptom is persistent muscle twitching that continues for weeks or months. Other commonly reported symptoms include muscle cramping, fatigue, exercise intolerance, tingling or buzzing sensations (often described as internal vibrations or internal tremors), and anxiety related to the ongoing nature of symptoms. Crucially, muscle weakness and wasting are NOT symptoms of BFS — their presence warrants immediate medical evaluation.'
  },
  {
    question: 'What triggers BFS twitching?',
    answer:
      'Common triggers include stress and anxiety, caffeine and stimulant intake, strenuous exercise, sleep deprivation, magnesium or electrolyte deficiency, and certain medications. Many people find that their twitching worsens during periods of high stress or after intense physical activity. Identifying and managing personal triggers is one of the most effective ways to reduce symptom frequency.'
  },
  {
    question: 'How long does BFS last?',
    answer:
      'BFS duration varies widely. Some people experience twitching for a few months following a trigger event (such as a viral illness or period of extreme stress) and see full resolution. Others report symptoms persisting for years. Most people with BFS do not see progression to more serious conditions. Prognosis is generally excellent, with many patients reporting significant improvement after lifestyle adjustments and reassurance from a neurologist.'
  },
  {
    question: 'How is BFS diagnosed?',
    answer:
      'BFS is primarily a clinical diagnosis — meaning it is based on a neurologist\'s examination and the exclusion of more serious conditions. A neurologist will assess muscle strength, reflexes, and coordination. An EMG (electromyography) may be performed to rule out conditions like ALS or other motor neuron diseases. Blood tests checking for electrolyte imbalances, thyroid function, and vitamin deficiencies are also common. There is no single definitive test for BFS itself.'
  }
];

// ─────────────────────────────────────────────
// BFS vs ALS COMPARISON DATA
// ─────────────────────────────────────────────
const COMPARISON_ROWS = [
  { feature: 'Muscle twitching (fasciculations)', bfs: true,  als: true,  note: 'Present in both — but context differs' },
  { feature: 'Progressive muscle weakness',        bfs: false, als: true,  note: 'Absent in BFS; hallmark of ALS' },
  { feature: 'Muscle wasting (atrophy)',            bfs: false, als: true,  note: 'No atrophy in BFS' },
  { feature: 'Normal muscle strength',             bfs: true,  als: false, note: 'Preserved strength is key to BFS diagnosis' },
  { feature: 'Anxiety & stress worsening symptoms',bfs: true,  als: false, note: 'BFS highly stress-reactive' },
  { feature: 'Internal tremors / vibrations',      bfs: true,  als: false, note: 'Very common BFS symptom' },
  { feature: 'Normal EMG possible',                bfs: true,  als: false, note: 'ALS always shows abnormal EMG' },
  { feature: 'Life expectancy affected',           bfs: false, als: true,  note: 'BFS has no impact on lifespan' },
];

// ─────────────────────────────────────────────
// TESTIMONIALS
// ─────────────────────────────────────────────
const TESTIMONIALS = [
  {
    quote:
      'After months of panic-Googling ALS, getting diagnosed with BFS by a neurologist changed my life. The twitching didn\'t stop overnight, but my anxiety did.',
    name: 'Sarah M.',
    detail: 'Diagnosed 2022 · Calf & thigh twitching',
    avatar: 'https://randomuser.me/api/portraits/women/47.jpg'
  },
  {
    quote:
      'Reducing caffeine and adding magnesium glycinate cut my twitching frequency by at least 60%. This community pointed me in that direction when my doctor wasn\'t sure what to suggest.',
    name: 'James T.',
    detail: 'Diagnosed 2021 · Widespread BFS',
    avatar: 'https://randomuser.me/api/portraits/men/11.jpg'
  },
  {
    quote:
      'The tongue twitching was the most frightening symptom for me. Finding others who shared the exact same experience on this site was genuinely life-changing.',
    name: 'Maria L.',
    detail: 'Diagnosed 2023 · Tongue fasciculations',
    avatar: 'https://randomuser.me/api/portraits/women/62.jpg'
  }
];

// ─────────────────────────────────────────────
// FAQ ACCORDION COMPONENT
// ─────────────────────────────────────────────
function FAQAccordion() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div>
      {FAQ_ITEMS.map((item, i) => (
        <div
          key={i}
          style={{
            borderBottom: '1px solid #e8e8e8',
            marginBottom: '0'
          }}
        >
          <button
            onClick={() => toggle(i)}
            aria-expanded={openIndex === i}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              padding: '1.1rem 0',
              textAlign: 'left',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              gap: '1rem'
            }}
          >
            <span
              style={{
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: '600',
                fontSize: '1rem',
                color: '#1a1a1a',
                lineHeight: 1.4
              }}
            >
              {item.question}
            </span>
            <span
              style={{
                flexShrink: 0,
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: openIndex === i ? '#c8102e' : '#f0f0f0',
                color: openIndex === i ? '#fff' : '#555',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.2rem',
                lineHeight: 1,
                transition: 'all 0.2s ease',
                fontWeight: '400'
              }}
            >
              {openIndex === i ? '−' : '+'}
            </span>
          </button>

          {openIndex === i && (
            <div
              style={{
                paddingBottom: '1.25rem',
                color: '#444',
                fontSize: '0.98rem',
                lineHeight: 1.75,
                fontFamily: "'Merriweather', serif"
              }}
            >
              {item.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────
function HomePage() {

  // Build JSON-LD schema objects
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer
      }
    }))
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalWebPage',
    name: 'Benign Fasciculation Syndrome (BFS) — Symptoms, Causes & Treatment',
    url: 'https://benignfasciculationsyndrome.org/',
    description:
      'Comprehensive information about Benign Fasciculation Syndrome (BFS): symptoms, causes, treatment options, and how to distinguish BFS from ALS.',
    medicalAudience: {
      '@type': 'MedicalAudience',
      audienceType: 'Patient'
    },
    reviewedBy: {
      '@type': 'Person',
      name: 'Dr. Simon Freilich',
      jobTitle: 'Neurologist'
    },
    lastReviewed: '2025-11-01'
  };

  return (
    <>
      <Helmet>
        <title>Benign Fasciculation Syndrome (BFS) | Symptoms, Causes, Treatment & ALS Differences</title>
        <meta
          name="description"
          content="What is Benign Fasciculation Syndrome? Learn BFS symptoms, causes, how it differs from ALS, and treatment options. Reviewed by neurologist Dr. Simon Freilich."
        />
        <link rel="canonical" href="https://benignfasciculationsyndrome.org/" />

        {/* Fonts */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&family=Merriweather:wght@400;700&display=swap"
          rel="stylesheet"
        />

        {/* JSON-LD structured data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      </Helmet>

      {/* ── Global font override ── */}
      <style>{`
        body, h1, h2, h3, h4, h5, h6, p, .btn, .navbar-brand, .nav-link, button {
          font-family: 'Montserrat', sans-serif !important;
        }
        .bfs-body-text {
          font-family: 'Merriweather', serif !important;
          font-size: 1.02rem;
          line-height: 1.85;
          color: #444;
        }
        .check-icon::before {
          content: '✓';
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          background: #e8f5e9;
          color: #2e7d32;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          margin-right: 10px;
          flex-shrink: 0;
        }
        .cross-icon::before {
          content: '✕';
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          background: #fce4e4;
          color: #c62828;
          border-radius: 50%;
          font-size: 0.75rem;
          font-weight: 700;
          margin-right: 10px;
          flex-shrink: 0;
        }
        @media (max-width: 767px) {
          .hero-title { font-size: 1.9rem !important; }
          .hero-subtitle { font-size: 1rem !important; }
          .comparison-table th, .comparison-table td { font-size: 0.78rem; padding: 8px 6px !important; }
          .stat-number { font-size: 1.75rem !important; }
        }
      `}</style>


      {/* ══════════════════════════════════════════════
          HERO — mobile-responsive, no squishing
      ══════════════════════════════════════════════ */}
      <Parallax
        bgImage="https://images.pexels.com/photos/1000445/pexels-photo-1000445.jpeg?auto=compress&cs=tinysrgb&w=1200"
        bgImageAlt="Calm nature scene representing Benign Fasciculation Syndrome support"
        strength={300}
      >
        <div
          style={{
            minHeight: '72vh',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            padding: '5rem 1.5rem'
          }}
        >
          {/* Dark overlay */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(135deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.35) 100%)',
              zIndex: 1
            }}
          />

          <Container style={{ position: 'relative', zIndex: 2 }}>
            <Row className="justify-content-center text-center">
              <Col lg={9}>
                {/* Eyebrow */}
                <div
                  style={{
                    display: 'inline-block',
                    background: 'rgba(200,16,46,0.85)',
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    padding: '0.3rem 1rem',
                    borderRadius: '999px',
                    marginBottom: '1.25rem'
                  }}
                >
                  Neurologist-reviewed information
                </div>

                <h1
                  className="hero-title"
                  style={{ fontWeight: '700', fontSize: 'clamp(1.9rem, 5vw, 3.1rem)', color: '#fff', lineHeight: 1.2, marginBottom: '0.75rem' }}
                >
                  Benign Fasciculation Syndrome (BFS)
                </h1>
                <h2
                  className="hero-subtitle"
                  style={{ fontWeight: '400', fontSize: 'clamp(1rem, 2.5vw, 1.35rem)', color: 'rgba(255,255,255,0.88)', marginBottom: '1.5rem', lineHeight: 1.5 }}
                >
                  Understand your muscle twitches. Get answers. Find community.
                </h2>

                <p
                  style={{
                    fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                    maxWidth: '580px',
                    margin: '0 auto 2rem',
                    color: 'rgba(255,255,255,0.82)',
                    lineHeight: 1.7
                  }}
                >
                  BFS is a benign neurological condition causing persistent muscle twitching. It is <strong style={{ color: '#fff' }}>not ALS</strong>. Thousands of people manage it successfully — and we're here to help.
                </p>

                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Button
                    variant="danger"
                    size="lg"
                    as={Link}
                    to="/about"
                    style={{ fontWeight: '600', borderRadius: '8px', padding: '0.7rem 2rem' }}
                  >
                    What Is BFS?
                  </Button>
                  <Button
                    variant="outline-light"
                    size="lg"
                    as={Link}
                    to="/contact"
                    style={{ fontWeight: '600', borderRadius: '8px', padding: '0.7rem 2rem' }}
                  >
                    Join the Community
                  </Button>
                </div>

                {/* Trust strip */}
                <div
                  style={{
                    marginTop: '2.5rem',
                    display: 'flex',
                    gap: '1.5rem',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    fontSize: '0.8rem',
                    color: 'rgba(255,255,255,0.72)',
                    letterSpacing: '0.03em'
                  }}
                >
                  <span>✓ Last reviewed November 2025</span>
                  <span>✓ Not medical advice — always consult your physician</span>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </Parallax>


      {/* ══════════════════════════════════════════════
          SOCIAL PROOF STATS BAR
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#111', color: '#fff', padding: '1.5rem 0' }}>
        <Container>
          <Row className="text-center gy-3">
            {[
              { value: '50,000+', label: 'People affected in the US' },
              { value: '98%',     label: 'Cases classified as benign' },
              { value: '1 in 100', label: 'Adults may experience BFS' },
              { value: '1,200+',  label: 'Community members supported' }
            ].map((stat, i) => (
              <Col xs={6} md={3} key={i}>
                <div
                  className="stat-number"
                  style={{ fontSize: '2rem', fontWeight: '700', color: '#c8102e', lineHeight: 1 }}
                >
                  {stat.value}
                </div>
                <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.35rem' }}>
                  {stat.label}
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          WHAT IS BFS — substantive content block (SEO)
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#fff', padding: '5rem 0' }}>
        <Container>
          <Row className="gy-5 align-items-start">
            {/* Main content */}
            <Col lg={7}>
              <div
                style={{
                  display: 'inline-block',
                  background: '#fce4e8',
                  color: '#c8102e',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '0.25rem 0.85rem',
                  borderRadius: '999px',
                  marginBottom: '1rem'
                }}
              >
                Condition Overview
              </div>

              <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: '#1a1a1a', marginBottom: '1.25rem', lineHeight: 1.3 }}>
                What Is Benign Fasciculation Syndrome?
              </h2>

              <div className="bfs-body-text">
                <p>
                  <strong>Benign Fasciculation Syndrome (BFS)</strong> is a neurological condition characterised by
                  involuntary, persistent muscle twitches — clinically known as <em>fasciculations</em> — that occur
                  without any underlying serious disease. The term "benign" is critical: it means that while the
                  twitching can be frequent and distressing, it poses no threat to long-term health, does not progress
                  to paralysis, and has no connection to life-threatening conditions.
                </p>

                <p>
                  Fasciculations in BFS can affect virtually any skeletal muscle in the body. The calves are the most
                  commonly reported site, followed by the thighs, feet, hands, and eyelids. Some people also experience
                  tongue twitching, which — despite being alarming — remains benign in the context of BFS. Many patients
                  also report accompanying symptoms such as <Link to="/blog/internal-vibrations-causes" style={{ color: '#c8102e' }}>internal tremors or internal vibrations</Link>,
                  a buzzing sensation felt deep inside the muscles that is not visible externally.
                </p>

                <p>
                  The exact cause of BFS is not fully understood. Current evidence points to hyperexcitability of the
                  peripheral motor neurons — the nerve pathways controlling muscle contraction. Stress, anxiety, sleep
                  deprivation, excessive caffeine, and strenuous exercise are all known to increase this
                  hyperexcitability and trigger or worsen twitching episodes.
                </p>

                <p>
                  The single most important thing to understand about BFS is that it is <strong>distinct from ALS
                  (Amyotrophic Lateral Sclerosis)</strong>. The key differentiator is muscle strength: BFS fasciculations
                  occur in muscles that remain completely strong and functional. ALS fasciculations are accompanied by
                  progressive weakness and atrophy. A neurologist can confirm the distinction with a physical
                  examination and, where warranted, an EMG test. See our full{' '}
                  <Link to="/blog/benign-fasciculation-syndrome-vs-als" style={{ color: '#c8102e' }}>
                    BFS vs ALS comparison guide
                  </Link>{' '}
                  for a detailed breakdown.
                </p>
              </div>

              {/* Key symptom checklist */}
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e8e8e8',
                  borderLeft: '4px solid #c8102e',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginTop: '2rem'
                }}
              >
                <h3 style={{ fontWeight: '700', fontSize: '1.05rem', marginBottom: '1rem', color: '#1a1a1a' }}>
                  Common BFS Symptoms
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem 1rem' }}>
                  {[
                    'Muscle twitching (fasciculations)',
                    'Internal tremors or vibrations',
                    'Muscle cramping',
                    'Fatigue & exercise intolerance',
                    'Tingling or buzzing sensations',
                    'Anxiety about symptoms',
                    'Eyelid or tongue twitching',
                    'Twitching worse at rest'
                  ].map((s, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', color: '#333' }}>
                      <span
                        style={{
                          flexShrink: 0,
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          background: '#e8f5e9',
                          color: '#2e7d32',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: '700',
                          marginRight: '8px'
                        }}
                      >
                        ✓
                      </span>
                      {s}
                    </div>
                  ))}
                </div>
                <p style={{ marginTop: '1rem', fontSize: '0.82rem', color: '#888', marginBottom: 0 }}>
                  ⚠ If you are experiencing <strong>muscle weakness, slurred speech, or swallowing difficulty</strong> alongside twitching, please consult a neurologist promptly — these symptoms are not associated with BFS.
                </p>
              </div>
            </Col>

            {/* Sidebar */}
            <Col lg={5}>
              {/* Doctor credit */}
              <div
                style={{
                  background: '#fff',
                  border: '1px solid #e8e8e8',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.06)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '1rem' }}>
                  <div
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      background: '#fce4e8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.3rem',
                      flexShrink: 0
                    }}
                  >
                    🩺
                  </div>
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '1rem', color: '#1a1a1a' }}>Dr. Simon Freilich</div>
                    <div style={{ fontSize: '0.82rem', color: '#666' }}>Neurologist</div>
                    <div style={{ fontSize: '0.78rem', color: '#aaa', marginTop: '2px' }}>Last reviewed: November 2025</div>
                  </div>
                </div>
                <p style={{ fontSize: '0.85rem', color: '#555', lineHeight: 1.65, marginBottom: '1rem' }}>
                  Content on this page has been reviewed for medical accuracy. This website is for informational purposes only and does not constitute medical advice. Always discuss symptoms with your personal physician.
                </p>
                <Button
                  variant="danger"
                  size="sm"
                  as={Link}
                  to="/understanding-muscle-twitches"
                  style={{ borderRadius: '6px', fontWeight: '600', fontSize: '0.85rem' }}
                >
                  Watch Dr. Freilich's Video
                </Button>
              </div>

              {/* Quick navigation */}
              <div
                style={{
                  background: '#f9fafb',
                  border: '1px solid #e8e8e8',
                  borderRadius: '12px',
                  padding: '1.5rem'
                }}
              >
                <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1a1a1a', marginBottom: '1rem' }}>
                  Key Resources
                </div>
                {[
                  { label: 'BFS vs ALS: Full Comparison', to: '/blog/benign-fasciculation-syndrome-vs-als', icon: '⚖️' },
                  { label: 'Understanding Tongue Twitching', to: '/blog/tongue-twitching-bfs', icon: '👅' },
                  { label: 'Internal Tremors & Vibrations', to: '/blog/internal-vibrations-causes', icon: '🫀' },
                  { label: 'How People Manage BFS', to: '/blog/how-i-cured-benign-fasciculation-syndrome', icon: '💪' },
                  { label: 'Lifestyle Tips for BFS', to: '/lifestyle-tips', icon: '🌿' },
                ].map((item, i) => (
                  <Link
                    key={i}
                    to={item.to}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '0.6rem 0',
                      borderBottom: i < 4 ? '1px solid #ebebeb' : 'none',
                      textDecoration: 'none',
                      color: '#333',
                      fontSize: '0.88rem',
                      fontWeight: '500',
                      transition: 'color 0.15s'
                    }}
                  >
                    <span style={{ fontSize: '1rem' }}>{item.icon}</span>
                    <span style={{ color: '#c8102e' }}>{item.label}</span>
                    <span style={{ marginLeft: 'auto', color: '#bbb', fontSize: '0.8rem' }}>→</span>
                  </Link>
                ))}
              </div>
            </Col>
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          YOUTUBE VIDEO SECTION
      ══════════════════════════════════════════════ */}
      <div
        style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f9fafc 40%, #f3f6fb 100%)',
          padding: '4.5rem 0',
          borderTop: '4px solid #c8102e'
        }}
      >
        <Container>
          <Row className="gy-4 align-items-center">
            <Col md={5}>
              <div
                style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '999px',
                  background: '#ffe5ea',
                  color: '#c8102e',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  marginBottom: '0.75rem'
                }}
              >
                Expert Insight
              </div>
              <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#222', marginBottom: '0.75rem' }}>
                What Is Benign Fasciculation Syndrome? A Neurologist Explains.
              </h2>
              <p style={{ fontSize: '1rem', color: '#555', lineHeight: 1.7, marginBottom: '1.25rem' }}>
                Dr. Simon Freilich walks through what BFS is, how it is diagnosed, why it is classified as
                benign, and how to differentiate it from more serious neurological conditions like ALS.
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  background: '#fff',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  borderLeft: '4px solid #c8102e',
                  boxShadow: '0 4px 14px rgba(0,0,0,0.06)',
                  marginBottom: '1.5rem'
                }}
              >
                <div style={{ fontSize: '1.6rem' }}>🩺</div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: '#999', textTransform: 'uppercase', fontWeight: '700', letterSpacing: '0.05em' }}>Featuring</div>
                  <div style={{ fontWeight: '700', color: '#222' }}>Dr. Simon Freilich</div>
                  <div style={{ fontSize: '0.8rem', color: '#777' }}>Board-Certified Neurologist</div>
                </div>
              </div>
              <ul style={{ paddingLeft: 0, listStyle: 'none', marginBottom: '1.5rem' }}>
                {['Common BFS symptoms and triggers', 'How BFS differs from ALS', 'What an EMG can and cannot tell you', 'Reassuring medical perspective on prognosis'].map((item, i) => (
                  <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '0.5rem', fontSize: '0.92rem', color: '#444' }}>
                    <span style={{ color: '#c8102e', fontWeight: '700', flexShrink: 0 }}>✓</span>
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                variant="danger"
                className="fw-semibold"
                style={{ borderRadius: '8px' }}
                as={Link}
                to="/understanding-muscle-twitches"
              >
                Read: Understanding Muscle Twitches
              </Button>
            </Col>

            <Col md={7}>
              <div
                style={{
                  position: 'relative',
                  paddingBottom: '56.25%',
                  height: 0,
                  overflow: 'hidden',
                  borderRadius: '16px',
                  boxShadow: '0 20px 50px rgba(0,0,0,0.16)'
                }}
              >
                <iframe
                  src="https://www.youtube.com/embed/xL1CJWlYumM?si=1FSCibkcMp7Hj57V"
                  title="Dr. Simon Freilich explains Benign Fasciculation Syndrome (BFS)"
                  style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
              <p style={{ fontSize: '0.82rem', color: '#999', marginTop: '0.75rem', textAlign: 'center' }}>
                Video content featuring Dr. Simon Freilich. Always discuss your own symptoms with your personal physician.
              </p>
            </Col>
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          BFS vs ALS COMPARISON TABLE
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#fff', padding: '5rem 0', borderTop: '1px solid #f0f0f0' }}>
        <Container>
          <Row className="mb-4 justify-content-center text-center">
            <Col lg={8}>
              <div
                style={{
                  display: 'inline-block',
                  background: '#fce4e8',
                  color: '#c8102e',
                  fontSize: '0.75rem',
                  fontWeight: '700',
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '0.25rem 0.85rem',
                  borderRadius: '999px',
                  marginBottom: '0.85rem'
                }}
              >
                Most Common Concern
              </div>
              <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', color: '#1a1a1a', marginBottom: '0.75rem' }}>
                BFS vs ALS: Understanding the Key Differences
              </h2>
              <p className="bfs-body-text" style={{ maxWidth: '620px', margin: '0 auto' }}>
                Anxiety about ALS is the number one concern reported by people with BFS. Here is a clear,
                side-by-side breakdown of how the two conditions compare. For a full analysis, see our{' '}
                <Link to="/blog/benign-fasciculation-syndrome-vs-als" style={{ color: '#c8102e' }}>
                  detailed BFS vs ALS guide
                </Link>.
              </p>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={10}>
              <div style={{ overflowX: 'auto', borderRadius: '12px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8' }}>
                <table className="comparison-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ background: '#1a1a1a', color: '#fff' }}>
                      <th style={{ padding: '14px 20px', textAlign: 'left', fontWeight: '700', width: '40%' }}>Feature</th>
                      <th style={{ padding: '14px 20px', textAlign: 'center', fontWeight: '700', color: '#4caf50', width: '25%' }}>BFS</th>
                      <th style={{ padding: '14px 20px', textAlign: 'center', fontWeight: '700', color: '#ef5350', width: '25%' }}>ALS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARISON_ROWS.map((row, i) => (
                      <tr
                        key={i}
                        style={{ background: i % 2 === 0 ? '#fafafa' : '#fff', borderBottom: '1px solid #f0f0f0' }}
                      >
                        <td style={{ padding: '13px 20px', color: '#333', fontWeight: '500' }}>
                          {row.feature}
                          <div style={{ fontSize: '0.75rem', color: '#999', fontWeight: '400', marginTop: '2px' }}>{row.note}</div>
                        </td>
                        <td style={{ padding: '13px 20px', textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: row.bfs ? '#e8f5e9' : '#fce4e4',
                              color: row.bfs ? '#2e7d32' : '#c62828',
                              fontWeight: '700',
                              fontSize: '0.9rem'
                            }}
                          >
                            {row.bfs ? '✓' : '✕'}
                          </span>
                        </td>
                        <td style={{ padding: '13px 20px', textAlign: 'center' }}>
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              background: row.als ? '#fce4e4' : '#e8f5e9',
                              color: row.als ? '#c62828' : '#2e7d32',
                              fontWeight: '700',
                              fontSize: '0.9rem'
                            }}
                          >
                            {row.als ? '✓' : '✕'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.8rem', color: '#aaa', textAlign: 'center' }}>
                This comparison is for educational purposes only. If you are experiencing muscle weakness or rapidly worsening neurological symptoms, seek immediate medical evaluation.
              </p>
              <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
                <Button
                  variant="danger"
                  as={Link}
                  to="/blog/benign-fasciculation-syndrome-vs-als"
                  style={{ borderRadius: '8px', fontWeight: '600' }}
                >
                  Read the Full BFS vs ALS Guide
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          FEATURED ARTICLES
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#f5f5f5', padding: '5rem 0' }}>
        <Container>
          <Row className="mb-4 justify-content-center text-center">
            <Col lg={7}>
              <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#1a1a1a' }}>
                In-Depth Guides
              </h2>
              <p style={{ fontSize: '1rem', color: '#666', marginTop: '0.5rem' }}>
                Comprehensive articles on BFS symptoms, causes, and management — written for patients, reviewed for accuracy.
              </p>
            </Col>
          </Row>
          <Row>
            {[
              {
                title: 'Understanding Muscle Twitches in BFS',
                text: 'How BFS fasciculations feel, where they occur, and how to tell them apart from more serious neurological symptoms.',
                img: 'https://images.pexels.com/photos/3952231/pexels-photo-3952231.jpeg?auto=compress&cs=tinysrgb&w=600',
                link: '/understanding-muscle-twitches',
                tag: 'Symptoms'
              },
              {
                title: 'Managing Stress & Anxiety with BFS',
                text: 'Stress is a major BFS trigger. Explore evidence-backed coping strategies, mindfulness techniques, and community support.',
                img: 'https://images.pexels.com/photos/6408284/pexels-photo-6408284.jpeg?auto=compress&cs=tinysrgb&w=600',
                link: '/managing-stress',
                tag: 'Management'
              },
              {
                title: 'Lifestyle Tips to Reduce BFS Twitching',
                text: 'Diet, exercise, caffeine, magnesium, and sleep — practical adjustments that many BFS patients find reduce symptom frequency.',
                img: 'https://images.pexels.com/photos/4056723/pexels-photo-4056723.jpeg?auto=compress&cs=tinysrgb&w=600',
                link: '/lifestyle-tips',
                tag: 'Lifestyle'
              }
            ].map((article, idx) => (
              <Col md={4} className="mb-4" key={idx}>
                <Card className="h-100" style={{ border: '1px solid #e8e8e8', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
                  <div style={{ height: '200px', overflow: 'hidden', position: 'relative' }}>
                    <Card.Img
                      variant="top"
                      src={article.img}
                      alt={`${article.title} — Benign Fasciculation Syndrome resource`}
                      loading="lazy"
                      style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        left: '12px',
                        background: '#c8102e',
                        color: '#fff',
                        fontSize: '0.7rem',
                        fontWeight: '700',
                        padding: '3px 10px',
                        borderRadius: '999px',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase'
                      }}
                    >
                      {article.tag}
                    </div>
                  </div>
                  <Card.Body style={{ padding: '1.25rem' }}>
                    <Card.Title style={{ fontWeight: '700', fontSize: '1rem', color: '#1a1a1a', marginBottom: '0.6rem' }}>
                      {article.title}
                    </Card.Title>
                    <Card.Text style={{ fontSize: '0.88rem', color: '#666', lineHeight: 1.65, marginBottom: '1.25rem' }}>
                      {article.text}
                    </Card.Text>
                    <Button
                      variant="danger"
                      as={Link}
                      to={article.link}
                      style={{ borderRadius: '7px', fontWeight: '600', fontSize: '0.88rem' }}
                    >
                      Read Guide →
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
          <div className="text-center mt-2">
            <Link to="/blog" style={{ color: '#c8102e', fontWeight: '600', fontSize: '0.92rem', textDecoration: 'none' }}>
              View all articles →
            </Link>
          </div>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#fff', padding: '5rem 0', borderTop: '1px solid #f0f0f0' }}>
        <Container>
          <Row className="mb-4 text-center justify-content-center">
            <Col lg={7}>
              <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#1a1a1a', marginBottom: '0.5rem' }}>
                Real Stories from the BFS Community
              </h2>
              <p style={{ color: '#666', fontSize: '0.95rem' }}>
                Thousands of people live well with BFS. Here are some of their experiences.
              </p>
            </Col>
          </Row>
          <Row className="gy-4">
            {TESTIMONIALS.map((t, i) => (
              <Col md={4} key={i}>
                <div
                  style={{
                    background: '#f9fafb',
                    border: '1px solid #e8e8e8',
                    borderTop: '4px solid #c8102e',
                    borderRadius: '12px',
                    padding: '1.75rem',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ fontSize: '2rem', color: '#c8102e', lineHeight: 1, marginBottom: '0.75rem', fontFamily: 'Georgia, serif' }}>"</div>
                  <p style={{ fontSize: '0.92rem', color: '#444', lineHeight: 1.75, fontStyle: 'italic', flexGrow: 1, marginBottom: '1.25rem' }}>
                    {t.quote}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: 'auto' }}>
                    <img
                      src={t.avatar}
                      alt={`${t.name} — BFS community member`}
                      loading="lazy"
                      style={{ width: '42px', height: '42px', borderRadius: '50%', objectFit: 'cover' }}
                    />
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '0.88rem', color: '#1a1a1a' }}>{t.name}</div>
                      <div style={{ fontSize: '0.78rem', color: '#888' }}>{t.detail}</div>
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          FAQ SECTION  (targets schema-rich keywords)
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#f9fafb', padding: '5rem 0', borderTop: '1px solid #f0f0f0' }}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={8}>
              <div className="text-center mb-4">
                <div
                  style={{
                    display: 'inline-block',
                    background: '#fce4e8',
                    color: '#c8102e',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    padding: '0.25rem 0.85rem',
                    borderRadius: '999px',
                    marginBottom: '0.85rem'
                  }}
                >
                  Frequently Asked Questions
                </div>
                <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#1a1a1a', marginBottom: '0.5rem' }}>
                  Your BFS Questions Answered
                </h2>
              </div>
              <FAQAccordion />
              <div style={{ textAlign: 'center', marginTop: '2.5rem' }}>
                <p style={{ color: '#666', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Have a question not answered here?
                </p>
                <Button
                  variant="danger"
                  as={Link}
                  to="/contact"
                  style={{ borderRadius: '8px', fontWeight: '600' }}
                >
                  Ask the Community
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          FIND SUPPORT — community CTA
      ══════════════════════════════════════════════ */}
      <div
        style={{
          background: '#c8102e',
          color: '#fff',
          padding: '5rem 0',
          textAlign: 'center'
        }}
      >
        <Container>
          <Row className="justify-content-center">
            <Col lg={7}>
              <h2 style={{ fontWeight: '700', marginBottom: '1rem', fontSize: 'clamp(1.5rem, 3vw, 2rem)' }}>
                You Are Not Alone
              </h2>
              <p style={{ fontSize: '1.05rem', maxWidth: '600px', margin: '0 auto 1.75rem', opacity: 0.9, lineHeight: 1.7 }}>
                Over 1,200 people with BFS have found support, answers, and community here. Whether you were
                just diagnosed or have been managing BFS for years, there is a place for you.
              </p>

              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
                {[
                  'https://randomuser.me/api/portraits/women/47.jpg',
                  'https://randomuser.me/api/portraits/men/11.jpg',
                  'https://randomuser.me/api/portraits/women/62.jpg',
                  'https://randomuser.me/api/portraits/men/95.jpg',
                  'https://randomuser.me/api/portraits/women/23.jpg'
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt="BFS community member"
                    loading="lazy"
                    style={{
                      width: '52px',
                      height: '52px',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid rgba(255,255,255,0.6)',
                      marginLeft: i > 0 ? '-12px' : 0
                    }}
                  />
                ))}
                <div
                  style={{
                    width: '52px',
                    height: '52px',
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.2)',
                    border: '3px solid rgba(255,255,255,0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.72rem',
                    fontWeight: '700',
                    marginLeft: '-12px'
                  }}
                >
                  1.2k+
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="light"
                  style={{ fontWeight: '600', borderRadius: '8px', color: '#c8102e', padding: '0.7rem 2rem' }}
                  as={Link}
                  to="/contact"
                >
                  Join the Community
                </Button>
                <Button
                  variant="outline-light"
                  style={{ fontWeight: '600', borderRadius: '8px', padding: '0.7rem 2rem' }}
                  as={Link}
                  to="/blog/how-i-cured-benign-fasciculation-syndrome"
                >
                  Read Member Stories
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          HOW YOU CAN HELP
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#f5f5f5', padding: '5rem 0' }}>
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 style={{ fontWeight: '700', fontSize: 'clamp(1.5rem, 3vw, 2rem)', color: '#1a1a1a' }}>
                How You Can Help
              </h2>
              <p style={{ fontSize: '1rem', color: '#666', maxWidth: '600px', margin: '0.5rem auto 0' }}>
                Your support funds BFS research, improves patient resources, and strengthens our community.
              </p>
            </Col>
          </Row>
          <Row>
            {[
              {
                title: 'Donate',
                text: 'Your contributions help fund BFS awareness campaigns, patient support resources, and ongoing research partnerships.',
                img: 'https://cdn-icons-png.flaticon.com/512/3349/3349234.png',
                link: '/donate'
              },
              {
                title: 'Volunteer',
                text: 'Share your time or expertise. Help moderate our forums, plan awareness events, or contribute to outreach efforts.',
                img: 'https://cdn-icons-png.flaticon.com/128/1006/1006555.png',
                link: '/contact'
              },
              {
                title: 'Partner With Us',
                text: 'Collaborate with our organisation to expand BFS awareness through medical communities, media, and research channels.',
                img: 'https://cdn-icons-png.flaticon.com/512/1077/1077012.png',
                link: '/contact'
              }
            ].map((item, idx) => (
              <Col md={4} className="mb-4" key={idx}>
                <Card
                  className="text-center h-100"
                  style={{
                    border: '1px solid #e8e8e8',
                    borderRadius: '12px',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                    transition: 'box-shadow 0.2s'
                  }}
                >
                  <Card.Body style={{ padding: '2rem 1.5rem' }}>
                    <img
                      src={item.img}
                      alt={`${item.title} icon`}
                      loading="lazy"
                      style={{ width: '56px', marginBottom: '1.25rem', opacity: 0.85 }}
                    />
                    <Card.Title style={{ fontWeight: '700', fontSize: '1.1rem', color: '#1a1a1a', marginBottom: '0.75rem' }}>
                      {item.title}
                    </Card.Title>
                    <Card.Text style={{ fontSize: '0.9rem', color: '#666', lineHeight: 1.65, marginBottom: '1.5rem' }}>
                      {item.text}
                    </Card.Text>
                    <Button
                      variant="danger"
                      as={Link}
                      to={item.link}
                      style={{ borderRadius: '7px', fontWeight: '600', fontSize: '0.88rem' }}
                    >
                      {item.title === 'Donate' ? 'Donate Now' : 'Learn More'}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </div>


      {/* ══════════════════════════════════════════════
          DISCLAIMER FOOTER STRIP
      ══════════════════════════════════════════════ */}
      <div style={{ background: '#f0f0f0', borderTop: '1px solid #e0e0e0', padding: '1.5rem 0' }}>
        <Container>
          <p
            style={{
              fontSize: '0.78rem',
              color: '#999',
              textAlign: 'center',
              marginBottom: 0,
              lineHeight: 1.6
            }}
          >
            <strong style={{ color: '#666' }}>Medical Disclaimer:</strong> The information on this website is for
            educational purposes only and does not constitute medical advice, diagnosis, or treatment. Always consult
            a qualified healthcare professional regarding any medical condition. Content reviewed by Dr. Simon Freilich,
            Neurologist. Last reviewed: November 2025.
          </p>
        </Container>
      </div>
    </>
  );
}

export default HomePage;